// features/projects/templates/professional/widgets/path-forward.js
// ═══════════════════════════════════════════════════════════
// THE PATH FORWARD — Tier-A widget
//
// Q: "What are the 3 highest-leverage things I should do next?"
// Portfolio-wide ranked next moves: due predictions, biggest
// unreviewed effects, missing hypotheses, pending data wires.
// ═══════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import { WidgetShell, LayerSection, CaveatStrip } from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

export default function PathForwardWidget({ featured }) {
  const data = useWidgetData();
  const moves = useMemo(() => composePathForward(data), [data]);

  if (data.loading) {
    return <WidgetShell label="The Path Forward"><div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div></WidgetShell>;
  }
  if (moves.length === 0) {
    return (
      <WidgetShell label={featured ? 'Today' : 'The Path Forward'}>
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No next moves surfaced. The project may be in a holding pattern or no findings have actionable state yet.
        </div>
      </WidgetShell>
    );
  }

  // Featured mode: render just the top-ranked move with a brass
  // left border — single highest-leverage thing to do today.
  if (featured) {
    const top = moves[0];
    return (
      <WidgetShell label="Today">
        <div style={{
          paddingLeft: 14,
          borderLeft: `3px solid ${L.brass}`,
        }}>
          <div style={{
            fontFamily: FONT.serif, fontSize: 16, fontWeight: 400,
            color: L.text, lineHeight: 1.4, marginBottom: 8,
          }}>
            {top.label}
          </div>
          <div style={{
            fontFamily: FONT.sans, fontSize: 12, color: L.mid,
            lineHeight: 1.6,
          }}>
            {top.why}
          </div>
        </div>
        {moves.length > 1 && (
          <div style={{
            marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${L.b1}`,
            fontFamily: FONT.mono, fontSize: 9, color: L.dim,
            letterSpacing: '0.06em',
          }}>
            {moves.length - 1} more move{moves.length === 2 ? '' : 's'} in queue
          </div>
        )}
      </WidgetShell>
    );
  }

  return (
    <WidgetShell label="The Path Forward">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {moves.map((m, i) => <MoveRow key={m.id} move={m} rank={i + 1} />)}
      </div>
      <LayerSection label="Takeaway" accent={L.gold}>
        The methodology compounds — every completed move makes the next one more defensible. Conversely, leaving Phase A unrun on a substantive finding leaks unverified claims into every downstream artifact.
      </LayerSection>
      <CaveatStrip text="Ranking is heuristic. Sample-size limits, external dependencies, or stakeholder timelines can flip the actual priority. Treat as suggestion, not directive." />
    </WidgetShell>
  );
}

function MoveRow({ move, rank }) {
  const priorityColor = move.priority === 'high' ? L.terra
    : move.priority === 'medium' ? L.gold : L.dim;
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '24px 8px 1fr auto', gap: 10,
      alignItems: 'baseline', padding: '8px 10px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${L.b1}`, borderRadius: 4,
    }}>
      <span style={{
        fontFamily: FONT.mono, fontSize: 11, color: L.dim, letterSpacing: '0.04em',
      }}>
        {String(rank).padStart(2, '0')}
      </span>
      <span style={{
        width: 6, height: 6, borderRadius: '50%', background: priorityColor, marginTop: 6,
      }} />
      <div>
        <div style={{
          fontSize: 12, color: L.text, fontFamily: FONT.sans, fontWeight: 500, lineHeight: 1.4,
        }}>{move.label}</div>
        <div style={{
          fontSize: 11, color: L.mid, fontFamily: FONT.sans, lineHeight: 1.5, marginTop: 3,
        }}>{move.why}</div>
      </div>
      <span style={{
        fontFamily: FONT.mono, fontSize: 9, color: priorityColor,
        letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
      }}>{move.priority}</span>
    </div>
  );
}

function composePathForward({ comparisons = [], hypotheses = [], predictions = [] }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const moves = [];

  const due = predictions.filter(p =>
    p.status === 'pending' && new Date(p.resolution_date) <= today
  );
  for (const p of due) {
    moves.push({
      id: `resolve-${p.id}`,
      priority: 'high',
      label: `Resolve due prediction: ${truncate(p.claim, 60)}`,
      why: `Resolution date (${formatDate(p.resolution_date)}) has passed. Mark confirmed / refuted / inconclusive based on current evidence.`,
    });
  }

  const substantive = comparisons.filter(isSubstantive);
  const noPhaseA = substantive.filter(c => !c.phase_a_status)
    .slice().sort((a, b) => Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0));
  for (const c of noPhaseA.slice(0, 2)) {
    const dyn = DYN_BY_KEY[c.dynamic_key];
    moves.push({
      id: `phase-a-${c.id}`,
      priority: 'high',
      label: `Run Phase A on ${dyn?.label || c.dynamic_key}`,
      why: `${c.verdict || 'Substantive finding'} with d=${(Number(c.effect_size) || 0).toFixed(2)} carries an unverified claim. ~30s + ~$0.20 per run.`,
    });
  }

  const hypByComp = new Set((hypotheses || []).map(h => h.comparison_id));
  const noHyp = substantive.filter(c => c.phase_a_status && !hypByComp.has(c.id));
  for (const c of noHyp.slice(0, 2)) {
    const dyn = DYN_BY_KEY[c.dynamic_key];
    moves.push({
      id: `register-hyp-${c.id}`,
      priority: 'medium',
      label: `Register parallel hypothesis for ${dyn?.label || c.dynamic_key}`,
      why: 'Phase A survived with no hypothesis registered. Without it, the finding has no falsifier on record.',
    });
  }

  const pending = comparisons.filter(c =>
    !c.verdict || ['pending', 'insufficient_data'].includes(String(c.verdict).toLowerCase())
  );
  if (pending.length > 0) {
    moves.push({
      id: 'wire-pending',
      priority: 'medium',
      label: `Wire data for ${pending.length} pending dynamic${pending.length === 1 ? '' : 's'}`,
      why: 'Each pending dynamic is a finding pulled out of indeterminacy. Without observations on both sides, the comparison cannot be evaluated.',
    });
  }

  const PRI = { high: 0, medium: 1, low: 2 };
  return moves.slice().sort((a, b) => (PRI[a.priority] ?? 9) - (PRI[b.priority] ?? 9)).slice(0, 3);
}

function isSubstantive(c) {
  return c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase());
}
function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return String(d); }
}
function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
